import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useAuth } from '@/hooks';
import naLogo from '@/assets/Nautic Alert Logo.png';

function formatWait(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { login, loginWithPasskey, isLoading, error, retryAfterSeconds, clearError } = useAuth();
  const [supportsPasskey] = useState(() => browserSupportsWebAuthn());
  const navigate = useNavigate();

  // Ticks down locally once the backend reports a lockout (429, repeated
  // failed attempts — see AccountAuthenticate.py), so the form re-enables
  // itself the moment the wait is over instead of the user retrying blindly
  // and just getting told to wait again.
  //
  // Every `setSecondsLeft` call below happens inside a timer callback, never
  // synchronously in the effect body itself — the seed value is deferred
  // through a zero-delay `setTimeout` for exactly the same reason the
  // decrementing calls live inside `setInterval`: state updates belong in the
  // callback that reacts to the external timer ticking, not in the effect's
  // own synchronous execution.
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  useEffect(() => {
    if (retryAfterSeconds == null) return;
    const seed = setTimeout(() => setSecondsLeft(retryAfterSeconds), 0);
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev == null || prev <= 1) {
          clearInterval(interval);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      clearTimeout(seed);
      clearInterval(interval);
    };
  }, [retryAfterSeconds]);

  const throttled = secondsLeft != null && secondsLeft > 0;
  const waitLabel = throttled ? formatWait(secondsLeft) : null;

  const goAfterLogin = (termsAccepted: boolean) =>
    navigate(termsAccepted ? '/devices' : '/terms', { replace: true });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Belt-and-suspenders alongside the disabled button below — Enter in a
    // field submits the form regardless of button state.
    if (throttled) return;
    clearError();

    const { success, termsAccepted } = await login(username, password);
    if (success) goAfterLogin(termsAccepted);
  };

  const handlePasskeyLogin = async () => {
    clearError();
    const { success, termsAccepted } = await loginWithPasskey();
    if (success) goAfterLogin(termsAccepted);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-navy-950">
      <div className="w-full max-w-md px-4">
        {/* Logo & Brand */}
        <div className="mb-8 text-center">
          <img
            src={naLogo}
            alt="Nautic Alert"
            className="mx-auto mb-4 h-16 object-contain"
          />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Vessel Monitoring Portal
          </p>
        </div>

        {/* Login Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-lg dark:border-navy-700 dark:bg-navy-900">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                {throttled ? `Too many failed attempts. Try again in ${waitLabel}.` : error}
              </div>
            )}

            <div>
              <label
                htmlFor="username"
                className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                autoFocus
                className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-slate-500 dark:focus:border-teal-500"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-slate-500 dark:focus:border-teal-500"
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || throttled || !username || !password}
              className="w-full rounded-lg bg-teal-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500/50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Signing in...
                </span>
              ) : throttled ? (
                `Try again in ${waitLabel}`
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Passwordless passkey sign-in */}
          {supportsPasskey && (
            <>
              <div className="my-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-slate-200 dark:bg-navy-700" />
                <span className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">or</span>
                <span className="h-px flex-1 bg-slate-200 dark:bg-navy-700" />
              </div>
              <button
                type="button"
                onClick={handlePasskeyLogin}
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-all hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-500/50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-slate-200 dark:hover:bg-navy-700"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3" />
                </svg>
                Sign in with a passkey
              </button>
            </>
          )}

          {/* Account help links */}
          <div className="mt-6 space-y-2 border-t border-slate-200 pt-6 text-center dark:border-navy-700">
            <Link
              to="/create-account"
              className="block text-sm text-teal-600 hover:text-teal-500 dark:text-teal-400 dark:hover:text-teal-300"
            >
              Create an Account
            </Link>
            <Link
              to="/forgot-username"
              className="block text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
            >
              Forgot Username?
            </Link>
            <Link
              to="/forgot-password"
              className="block text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
            >
              Forgot Password?
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
          &copy; {new Date().getFullYear()} Nautic Alert. All rights reserved.
        </p>
      </div>
    </div>
  );
}
